import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { DCAStrategyList } from './DCAStrategyList';
import { DCACreateForm } from './DCACreateForm';
import { DCAStats } from './DCAStats';
import { DCAHistory } from './DCAHistory';
import { useWallet } from '../wallet/WalletProvider';
import { Plus, TrendingUp, Zap, Brain } from 'lucide-react';

export function DCADashboard() {
  const { isConnected } = useWallet();
  const [activeTab, setActiveTab] = useState('strategies');
  const [showCreateForm, setShowCreateForm] = useState(false);

  if (!isConnected) {
    return (
      <Card className="max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-violet-500" />
            DCA Inteligente
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-slate-600 dark:text-slate-400">
            Conecta tu wallet para crear estrategias de DCA automatizadas con IA.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-violet-500" />
            DCA Inteligente
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">
            Automatiza tus compras con Dollar Cost Averaging potenciado por IA
          </p>
        </div>
        <Button
          onClick={() => setShowCreateForm(true)}
          className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nueva Estrategia
        </Button>
      </div>

      {/* Feature Highlights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border-violet-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-violet-500/20 rounded-lg">
              <Brain className="h-5 w-5 text-violet-500" />
            </div>
            <div>
              <p className="font-medium text-slate-900 dark:text-slate-100">Smart Timing</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">IA optimiza el momento de compra</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <TrendingUp className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="font-medium text-slate-900 dark:text-slate-100">Dip Buying</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Compra mas en las caidas</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Zap className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <p className="font-medium text-slate-900 dark:text-slate-100">Gas Optimizado</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Ejecuta cuando el gas es bajo</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Overview */}
      <DCAStats />

      {/* Create Form Modal */}
      {showCreateForm && (
        <DCACreateForm onClose={() => setShowCreateForm(false)} />
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="strategies">Mis Estrategias</TabsTrigger>
          <TabsTrigger value="history">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="strategies" className="space-y-6">
          <DCAStrategyList />
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <DCAHistory />
        </TabsContent>
      </Tabs>
    </div>
  );
}
